import { AutoComplete } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../../../hooks/use-app-selector.ts"
import { type BaseApiResponse } from "../../../../../../../types/api.ts"
import { getEntityName } from "../../../../../../../utils/user/get-entity-name"
import { type CreateSupplierData } from "../../../../../../SuppliersPage/api/services.types.ts"
import { SupplierFormModal } from "../../../../../../SuppliersPage/components/SupplierFormModal.tsx"
import { createSupplier } from "../../../../../../SuppliersPage/redux/suppliers.thunks.ts"
import { type SupplierListItem } from "../../../../../../SuppliersPage/types/types.ts"
import { buildStakeholderOptions, normalize } from "./buildStakeholderOptions.ts"


/**
 * A typed name may be created as a supplier on the fly only when it does not
 * already match an existing option. Exported for a direct unit test.
 */
export function canCreateSupplier(
  typed: string,
  options: BaseOptionType[]
): boolean {
  const name = typed.trim()
  if (!name) return false
  return !options.some(
    (option) => normalize(String(option.value ?? "")) === normalize(name)
  )
}

interface SupplierAutoCompleteWithCreateProps {
  suppliers?: BaseOptionType[]
  value?: string | null
  onChange?: (value: string | null) => void
}

/**
 * The picker offers to create a Supplier but does not own the field: the row
 * commits free text either way. Why `onChange` stays bound, why cancelling
 * keeps the typed name, and why the search text is cleared are all recorded in
 * `docs/kb/decisions/2026-08-26-supplier-picker-creates-a-record-without-owning-the-field.md`.
 */
export function SupplierAutoCompleteWithCreate({
  suppliers,
  value,
  onChange
}: SupplierAutoCompleteWithCreateProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const canCreateSuppliers = useAppSelector(
    (state) =>
      !!state.auth?.permissions?.codes?.suppliers?.suppliers?.supplier_create
  )

  const [partySearch, setPartySearch] = useState("")
  const [createdSupplierOptions, setCreatedSupplierOptions] = useState<
    BaseOptionType[]
  >([])
  const [modalOpen, setModalOpen] = useState(false)
  const [pendingName, setPendingName] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const stakeholderOptions = [
    ...createdSupplierOptions,
    ...buildStakeholderOptions(suppliers)
  ]
  const typedName = partySearch.trim()
  const offerCreate =
    canCreateSuppliers &&
    canCreateSupplier(typedName, stakeholderOptions)
  const partyOptions = offerCreate
    ? [
        ...stakeholderOptions,
        {
          value: typedName,
          label: t("suppliers.party.addOption", { name: typedName })
        }
      ]
    : stakeholderOptions

  const closeModal = () => {
    setModalOpen(false)
    setPendingName(null)
  }

  const handleSave = (data: CreateSupplierData) => {
    setSaving(true)
    dispatch(createSupplier(data))
      .unwrap()
      .then((response: BaseApiResponse<SupplierListItem>) => {
        const created = response.details.data
        // getEntityName's own default is "-", which would commit as the payee
        // name and read as deliberate. Fall back to what the user typed.
        const composedName = getEntityName(created, "") || (pendingName ?? "")
        if (composedName) {
          setCreatedSupplierOptions((prev) => [
            { value: composedName, label: composedName },
            ...prev
          ])
          onChange?.(composedName)
        }
        setPartySearch("")
        closeModal()
      })
      .catch(() => {})
      .finally(() => setSaving(false))
  }

  return (
    <>
      <AutoComplete
        className="w-full"
        placeholder={t("suppliers.party.supplierPlaceholder")}
        value={value ?? undefined}
        options={partyOptions}
        allowClear
        onSearch={setPartySearch}
        // Bound deliberately: unbinding breaks combobox mode both ways.
        onChange={(next) => onChange?.((next as string | undefined) ?? null)}
        onSelect={(selected) => {
          // Value-wise a no-op; this only opens the modal.
          if (offerCreate && selected === typedName) {
            setPendingName(selected)
            setModalOpen(true)
            return
          }
          onChange?.(selected)
          // Or the dropdown reopens still offering the stale typed name.
          setPartySearch("")
        }}
        onClear={() => {
          onChange?.(null)
          setPartySearch("")
        }}
        filterOption={(input, option) =>
          normalize(String(option?.value ?? "")).includes(normalize(input))
        }
      />
      {modalOpen && (
        <SupplierFormModal
          initialValues={null}
          defaultName={pendingName ?? undefined}
          saving={saving}
          onSave={handleSave}
          onCancel={closeModal}
        />
      )}
    </>
  )
}
